import React, { useState, useMemo } from 'react';
import { Package, User, Search, X } from 'lucide-react';

function OrdersList({ orders, onItemClick }) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter orders based on search term
  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) return orders;

    const search = searchTerm.toLowerCase().trim();
    
    return orders.filter((order) => {
      // Search by ID
      if (order.id.toString().includes(search)) return true;
      
      // Search by customer name
      if (order.customer_name?.toLowerCase().includes(search)) return true;
      
      // Search by phone (telefono)
      if (order.telefono?.includes(search)) return true;
      
      // Search by driver ID
      if (order.driver_id && order.driver_id.toString().includes(search)) return true;
      
      return false;
    });
  }, [orders, searchTerm]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      encamino: 'bg-blue-100 text-blue-800 border-blue-300',
      completado: 'bg-green-100 text-green-800 border-green-300',
      cancelado: 'bg-gray-100 text-gray-800 border-gray-300',
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-300';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En camino',
      completado: 'Completado',
      cancelado: 'Cancelado',
    };
    return labels[status] || status;
  };

  const truncateName = (name, maxLength = 15) => {
    if (!name) return 'Sin nombre';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-[#B1336E] px-4 py-3 border-b border-[#B1336E]/20">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-white font-bold text-sm flex items-center gap-2 font-montserrat">
            <Package className="w-4 h-4" />
            Recojos ({orders.length})
          </h2>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
          <input
            type="text"
            placeholder="Buscar por ID, nombre, teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 font-montserrat"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Results count when searching */}
        {searchTerm && (
          <p className="text-white/70 text-xs mt-2 font-montserrat">
            {filteredOrders.length} resultado{filteredOrders.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <Package className="w-12 h-12 text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm font-montserrat">
              {searchTerm 
                ? 'No se encontraron recojos con ese criterio'
                : 'No hay recojos para esta fecha'
              }
            </p>
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="mt-3 text-[#B1336E] hover:text-[#B1336E]/80 text-sm font-medium font-montserrat"
              >
                Limpiar búsqueda
              </button>
            )}
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {filteredOrders.map((order) => (
              <button
                key={order.id}
                onClick={() => onItemClick(order.id)}
                className="w-full text-left bg-white hover:bg-slate-50 border border-slate-200 rounded-lg p-3 transition-all hover:shadow-md"
              >
                {/* ID and Status */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[#B1336E] font-bold text-sm font-montserrat">
                      #{order.id}
                    </span>
                    {order.sequence && (
                      <span className="bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded font-montserrat">
                        Seq: {order.sequence}
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded border font-medium font-montserrat ${getStatusColor(
                      order.status
                    )}`}
                  >
                    {getStatusLabel(order.status)}
                  </span>
                </div>

                {/* Customer Name */}
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-800 text-sm font-medium font-montserrat">
                    {truncateName(order.customer_name)}
                  </span>
                </div>

                {/* Phone */}
                {order.telefono && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-slate-600 text-xs font-montserrat">
                      {order.telefono}
                    </span>
                  </div>
                )}

                {/* Driver Info */}
                {order.driver_id && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <span className="text-xs text-slate-500 font-montserrat">
                      Conductor: ID {order.driver_id}
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrdersList;